import React from 'react';

import type { PredicateFilter } from '@common/components/predicates-filter/predicates-filter';
import { EPredicateFields } from '@common/definitions';

import type { CustomUserPropertyPredicateFilter }
from '@modules/user-properties/utils/build-custom-user-property-predicate-filters';
import { isCustomUserPropertyAttribute, isPredicateBooleanValueSet }
from '@modules/user-properties/utils/custom-user-property-predicate';

export function isPredicateValueEmpty(
  value: unknown,
  filter?: PredicateFilter | CustomUserPropertyPredicateFilter
): boolean {
  if (typeof value === 'boolean') return false;

  if (Array.isArray(value)) return value.length === 0;

  if (isCustomUserPropertyPredicateFilter(filter) && filter.propertyType === 'boolean') {
    return !isPredicateBooleanValueSet(value);
  }

  return value === null || value === undefined || value === '';
}

export function resolvePredicateFilters(
  customPropertyFilters: Record<string, CustomUserPropertyPredicateFilter> = {}
): Record<string, PredicateFilter | CustomUserPropertyPredicateFilter> {
  return {
  // EPredicateFilters spread happens at call site to avoid circular imports
    ...customPropertyFilters,
  };
}

export function getPredicateFilterLabel(
  filter: PredicateFilter | CustomUserPropertyPredicateFilter
): React.ReactNode {
  if ('isCustomUserProperty' in filter && filter.isCustomUserProperty) {
    return filter.label;
  }

  return null;
}

export function shouldDisablePredicateFilter(
  filterValue: string,
  existingFields: string[],
  multipleInstanceFilters: string[] = [
    EPredicateFields.DAYS_IN_SERVICE,
    EPredicateFields.DAYS_BEFORE_OUT_OF_SERVICE,
  ]
): boolean {
  if (multipleInstanceFilters.includes(filterValue)) return false;
  return existingFields.includes(filterValue);
}

export function sortCustomUserPropertyFilters(
  filters: CustomUserPropertyPredicateFilter[]
): CustomUserPropertyPredicateFilter[] {
  return [...filters].sort((a, b) => a.label.localeCompare(b.label));
}

export function isCustomUserPropertyPredicateFilter(
  filter: PredicateFilter | CustomUserPropertyPredicateFilter | undefined
): filter is CustomUserPropertyPredicateFilter {
  return !!filter && 'isCustomUserProperty' in filter && filter.isCustomUserProperty === true;
}

export function getCustomUserPropertyPredicateLabel(
  attribute: string,
  filtersMap: Record<string, CustomUserPropertyPredicateFilter>
): string | null {
  if (!isCustomUserPropertyAttribute(attribute)) return null;
  return filtersMap[attribute]?.label ?? null;
}
